import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardContent, Heading, Text, Flex, DataTable, LoadingOrbit } from '@chatsdet/ui';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { runbookRunColumns } from '../../../features/runbooks/columns';
import type { RunbookRun } from '../../../features/runbooks/types';

interface RunbookRunHistoryProps {
  propertyId: string;
  onSelectRun: (runId: string) => void;
}

/** Newest-first run history for a property, below the runbook list. */
export function RunbookRunHistory({ propertyId, onSelectRun }: RunbookRunHistoryProps) {
  const membersQuery = useOrgMembers();

  const runsQuery = useQuery({
    queryKey: ['property-runbook-runs', propertyId],
    queryFn: async (): Promise<{ ok: boolean; items: RunbookRun[] }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/runbook-runs`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch run history');
      return res.json();
    },
  });

  const runs = runsQuery.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <Heading level={3}>Run History</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Runbook executions for this property, newest first
        </Text>
      </CardHeader>
      <CardContent>
        {runsQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-8">
            <LoadingOrbit size="md" />
          </Flex>
        ) : runsQuery.isError ? (
          <Text size="sm" className="text-destructive">
            Couldn&apos;t load run history. Reload the page to try again.
          </Text>
        ) : runs.length > 0 ? (
          <DataTable
            columns={runbookRunColumns(membersQuery.data?.items ?? [])}
            data={runs}
            onRowClick={(row) => onSelectRun(row.id)}
          />
        ) : (
          <div className="text-center py-8">
            <Text size="sm" className="text-muted-foreground">
              No runs yet.
            </Text>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
